/**
 * Reglas de formato y tamaño de la carga documental, del lado del cliente.
 *
 * La fuente de verdad es `kernel.tipo_documento`: el backend resuelve por cada
 * tipo qué formatos acepta y con qué límite (los topes por formato pueden ser
 * menores que el del tipo) y los manda en `formatosPermitidos`. Aquí sólo se
 * presenta esa información y se hace la validación previa, con los mismos
 * mensajes que devuelve el backend para que el usuario lea siempre lo mismo
 * falle donde falle.
 */

/** Formato habilitado para un tipo documental, con su límite ya resuelto. */
export interface FormatoPermitido {
  /** Nombre técnico: PDF, DOCX, HEIC… */
  formato: string;
  /** Nombre para mostrar: "PDF", "HEIC/HEIF". */
  etiqueta: string;
  /** MIME canónico y alias que declaran los navegadores. */
  mimeTypes: string[];
  /** Extensiones con punto: ".jpg", ".jpeg". */
  extensiones: string[];
  tamanoMaximoBytes: number;
}

export interface TipoDocumento {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  mimeTypesPermitidos: string[];
  tamanoMaximoBytes: number;
  requiereCopiaCertificada: boolean;
  requeridoTelecomBasico: boolean;
  tags: string[];
  /** Puede faltar si el backend aún no expone el desglose por formato. */
  formatosPermitidos?: FormatoPermitido[];
}

/**
 * Valor del atributo `accept` del input: MIME y extensiones juntos. Las
 * extensiones importan porque Windows y varios Android no adivinan el MIME de
 * HEIC ni de los ofimáticos, y sin ellas esos archivos aparecen en gris en el
 * explorador.
 */
export function acceptDe(tipo: TipoDocumento | undefined): string | undefined {
  if (!tipo) return undefined;
  const formatos = formatosDe(tipo);
  if (formatos.length === 0) {
    return tipo.mimeTypesPermitidos.length > 0 ? tipo.mimeTypesPermitidos.join(",") : undefined;
  }
  const valores = formatos.flatMap((f) => [...f.mimeTypes, ...f.extensiones]);
  return Array.from(new Set(valores)).join(",");
}

/** "PDF, DOCX, JPG" — lo que el usuario puede subir en este requisito. */
export function etiquetasDe(tipo: TipoDocumento | undefined): string {
  if (!tipo) return "";
  const formatos = formatosDe(tipo);
  if (formatos.length > 0) return formatos.map((f) => f.etiqueta).join(", ");
  return tipo.mimeTypesPermitidos.join(", ");
}

/**
 * Texto del límite de tamaño. Cuando los formatos tienen topes distintos lo
 * dice explícitamente ("50 MB · imágenes 15 MB") en vez de anunciar un máximo
 * que no aplica a todos.
 */
export function limiteDe(tipo: TipoDocumento | undefined): string {
  if (!tipo) return "";
  const formatos = formatosDe(tipo);
  if (formatos.length === 0) return formatMB(tipo.tamanoMaximoBytes);

  const porLimite = new Map<number, string[]>();
  for (const f of formatos) {
    porLimite.set(f.tamanoMaximoBytes, [...(porLimite.get(f.tamanoMaximoBytes) ?? []), f.etiqueta]);
  }
  if (porLimite.size === 1) return formatMB(formatos[0].tamanoMaximoBytes);

  return Array.from(porLimite.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([bytes, etiquetas]) => `${etiquetas.join("/")} ${formatMB(bytes)}`)
    .join(" · ");
}

/**
 * Valida antes de enviar. Devuelve el mensaje a mostrar o `null` si el archivo
 * pasa. No sustituye a la validación del backend —que además revisa el
 * contenido real del archivo—, sólo evita el viaje de ida y vuelta.
 */
export function validarArchivo(tipo: TipoDocumento | undefined, file: File): string | null {
  if (!tipo) return null;
  const formatos = formatosDe(tipo);

  if (formatos.length === 0) {
    // Catálogo sin desglose (backend anterior): sólo se puede revisar tamaño.
    return file.size > tipo.tamanoMaximoBytes ? mensajeTamano(tipo.tamanoMaximoBytes) : null;
  }

  const formato = formatoDeArchivo(file, formatos);
  if (!formato) {
    return `El formato del archivo no está permitido para este tipo de documento. Formatos permitidos: ${etiquetasDe(
      tipo,
    )}.`;
  }
  if (file.size > formato.tamanoMaximoBytes) {
    return mensajeTamano(formato.tamanoMaximoBytes);
  }
  return null;
}

/** Formato al que corresponde el archivo: por extensión y, si no la trae, por MIME. */
function formatoDeArchivo(file: File, formatos: FormatoPermitido[]): FormatoPermitido | undefined {
  const nombre = file.name.toLowerCase();
  const porExtension = formatos.find((f) => f.extensiones.some((ext) => nombre.endsWith(ext)));
  if (porExtension) return porExtension;
  if (!file.type) return undefined;
  const mime = file.type.toLowerCase();
  return formatos.find((f) => f.mimeTypes.some((m) => m.toLowerCase() === mime));
}

function formatosDe(tipo: TipoDocumento): FormatoPermitido[] {
  return tipo.formatosPermitidos ?? [];
}

function mensajeTamano(bytes: number): string {
  return `El archivo supera el tamaño máximo permitido para este documento (${formatMB(bytes)}).`;
}

export function formatMB(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
